import type { MapEntityPath } from '@common/schemas/entities/location.schema';
import type { PlacedSymbol } from './types';

export type Vertex = { x: number; y: number };

interface JunctionInfo {
    parentPath: MapEntityPath;
    ratio: number;
}

/**
 * Checks if a river's end vertex is snapped to another river path, returning junction info.
 */
export function getRiverJunctionInfo(
    endVertex: Vertex,
    paths: MapEntityPath[],
    currentPathUid: string,
): JunctionInfo | null {
    const snapThreshold = 1.5;
    for (const path of paths) {
        if (path.pathType !== 'river' || path.uid === currentPathUid) continue;

        let totalParentLength = 0;
        const segmentLengths: number[] = [];
        for (let i = 0; i < path.vertices.length - 1; i++) {
            const d = Math.hypot(path.vertices[i + 1].x - path.vertices[i].x, path.vertices[i + 1].y - path.vertices[i].y);
            segmentLengths.push(d);
            totalParentLength += d;
        }

        if (totalParentLength === 0) continue;

        let accumulatedLength = 0;
        for (let i = 0; i < path.vertices.length - 1; i++) {
            const a = path.vertices[i];
            const b = path.vertices[i + 1];
            const segLen = segmentLengths[i];

            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const lenSq = dx * dx + dy * dy;
            if (lenSq === 0) continue;

            const t = Math.max(0, Math.min(1, ((endVertex.x - a.x) * dx + (endVertex.y - a.y) * dy) / lenSq));
            const projX = a.x + t * dx;
            const projY = a.y + t * dy;

            if (Math.hypot(endVertex.x - projX, endVertex.y - projY) < snapThreshold) {
                const junctionLen = accumulatedLength + t * segLen;
                return { parentPath: path, ratio: junctionLen / totalParentLength };
            }
            accumulatedLength += segLen;
        }
    }
    return null;
}

/**
 * Returns the meandered width of a parent river at the junction snap ratio.
 */
export function getRiverJunctionWidth(
    endVertex: Vertex,
    paths: MapEntityPath[],
    currentPathUid: string,
    defaultWidth: number,
): number {
    const jInfo = getRiverJunctionInfo(endVertex, paths, currentPathUid);
    if (!jInfo) return defaultWidth;
    const parentWidthFactor = 0.25 + 0.75 * jInfo.ratio;
    return jInfo.parentPath.style.strokeWidth * parentWidthFactor;
}

/**
 * Computes the meandered point coordinates along a parent river at ratio u.
 */
export function getParentMeanderedPointAtRatio(
    parentPath: MapEntityPath,
    ratio: number,
    placedSymbols: PlacedSymbol[],
): Vertex {
    const pts = getOrganicPathPoints(parentPath.vertices, 'river', placedSymbols, [], '');
    if (pts.length === 0) return parentPath.vertices[parentPath.vertices.length - 1];

    let totalLen = 0;
    const lens: number[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
        const d = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
        lens.push(d);
        totalLen += d;
    }

    const targetLen = totalLen * ratio;
    let acc = 0;
    for (let i = 0; i < pts.length - 1; i++) {
        const seg = lens[i];
        if (acc + seg >= targetLen) {
            const t = (targetLen - acc) / seg;
            return {
                x: pts[i].x + (pts[i + 1].x - pts[i].x) * t,
                y: pts[i].y + (pts[i + 1].y - pts[i].y) * t,
            };
        }
        acc += seg;
    }
    return pts[pts.length - 1];
}

/**
 * Generates organic meandered path geometry using Chaikin subdivision and prime-FBM noise.
 * Locks snapped tributaries to the exact meandered points of their parent river.
 */
export function getOrganicPathPoints(
    vertices: Vertex[],
    pathType: string,
    placedSymbols: PlacedSymbol[],
    allPaths: MapEntityPath[] = [],
    currentPathUid: string = '',
): Vertex[] {
    if (vertices.length < 2) return vertices;

    let pts: Vertex[] = [];
    for (let i = 0; i < vertices.length - 1; i++) {
        const p0 = vertices[i];
        const p1 = vertices[i + 1];
        const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
        const segmentCount = Math.max(8, Math.floor(dist / 6));
        for (let j = 0; j < segmentCount; j++) {
            const t = j / segmentCount;
            pts.push({ x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t });
        }
    }
    pts.push(vertices[vertices.length - 1]);

    const smoothingIterations = pathType === 'river' ? 3 : 2;
    for (let iter = 0; iter < smoothingIterations; iter++) {
        if (pts.length < 3) break;
        const nextPts: Vertex[] = [pts[0]];
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i];
            const p1 = pts[i + 1];
            nextPts.push({ x: p0.x * 0.75 + p1.x * 0.25, y: p0.y * 0.75 + p1.y * 0.25 });
            nextPts.push({ x: p0.x * 0.25 + p1.x * 0.75, y: p0.y * 0.25 + p1.y * 0.75 });
        }
        nextPts.push(pts[pts.length - 1]);
        pts = nextPts;
    }

    let snapTarget: Vertex | null = null;
    if (pathType === 'river' && vertices.length >= 2 && allPaths.length > 0) {
        const endVertex = vertices[vertices.length - 1];
        const jInfo = getRiverJunctionInfo(endVertex, allPaths, currentPathUid);
        if (jInfo) {
            snapTarget = getParentMeanderedPointAtRatio(jInfo.parentPath, jInfo.ratio, placedSymbols);
        }
    }

    const noiseAmp = pathType === 'river' ? 5.0 : pathType === 'road' ? 0.7 : 0.4;
    return pts.map((p, idx) => {
        const t = idx / (pts.length - 1);

        if (idx === pts.length - 1) {
            return snapTarget ? snapTarget : p;
        }
        if (idx === 0) return p;

        const prev = pts[idx - 1];
        const next = pts[idx + 1];
        const dx = next.x - prev.x;
        const dy = next.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len;
        const ny = dx / len;

        const d = idx * 6;
        const meander = Math.sin(d * 0.011) * 1.0 +
                        Math.sin(d * 0.027 + 1.6) * 0.45 +
                        Math.sin(d * 0.059 - 0.7) * 0.18;

        const shake = Math.sin(d * 0.5) * 0.25;
        const dampening = snapTarget ? Math.pow(1 - t, 2.5) : 1.0;
        const offset = (meander * noiseAmp + (pathType === 'river' ? shake : shake * 0.5)) * dampening;

        let mx = p.x + nx * offset;
        let my = p.y + ny * offset;

        if (snapTarget && t > 0.8) {
            const blend = (t - 0.8) / 0.2;
            mx = mx * (1 - blend) + snapTarget.x * blend;
            my = my * (1 - blend) + snapTarget.y * blend;
        }

        for (const sym of placedSymbols) {
            const dist = Math.hypot(mx - sym.x, my - sym.y);
            const repelRadius = sym.size * 0.35;
            if (dist < repelRadius) {
                const push = (repelRadius - dist) * 0.85;
                const rx = (mx - sym.x) / (dist || 1);
                const ry = (my - sym.y) / (dist || 1);
                mx += rx * push;
                my += ry * push;
            }
        }

        return { x: mx, y: my };
    });
}
